import { mapGetters } from 'vuex'
import Vue from 'vue'
import Configuration from '@/agilis/util/Configuration'
import ConfigEditor from './ConfigEditor/ConfigEditor.vue'
import { isUndefined } from 'lodash'

export default {
  components: {
    ConfigEditor
  },

  data () {
    return {
      selectedUser: null,
      serviceConfiguration: {},
      remoteTrackers: {},
      selectedWorkspace: null,
      workspaceRoles: null
    }
  },
  async mounted () {
    if (this.aboutApplications) {
      for (const appId in this.aboutApplications) {
        this.setAppConfigs(appId)
      }
    }
  },

  computed: {
    ...mapGetters({
      computedApplications: 'configurationStore/applications',
      computedPrivileges: 'userDataStore/privileges',
      userIsAdmin: 'userDataStore/userIsAdmin',
      authorizedApplications: 'configurationStore/auth/applications',
      agilisVersion: 'configurationStore/version',
      theme: 'themeStore/getTheme'
    }),
    docURL () {
      return `/vuepress/${this.theme}/index.html`
    },
    aboutApplications () {
      let aboutApplications = this.authorizedApplications
      if (this.userCanEditConfig) {
        aboutApplications = this.computedApplications
      }
      return aboutApplications
    },

    userCanImpersonate () {
      return this.userIsAdmin || Configuration.auth.userHasPrivilege('/admin/impersonate')
    },

    userCanEditConfig () {
      return this.userIsAdmin || Configuration.auth.userHasPrivilege('/admin/editConfig')
    },

    computedVersion () {
      return process.env.VUE_APP_AGILIS_PACKAGE_VERSION
    },

    computedApplicationName () {
      return appName => {
        return this.$t(appName)
      }
    },

    computedApplicationVersion () {
      return appVersion => {
        return ' ' + appVersion
      }
    },

    computedServiceAppVersion () {
      return appId => {
        let html = ''
        if (this.serviceConfiguration[appId]) {
          const appConfiguratorVersion = this.aboutApplications[appId].version
          const appServiceVersion = this.serviceConfiguration[appId].version
          if (appConfiguratorVersion !== appServiceVersion) {
            html = ` ❌ (server ${appServiceVersion}) `
          }
        } else if (this.serviceConfiguration[appId] === null) {
          html = ''
        } else {
          html = ' ❌ (server n/a)'
        }
        return html
      }
    },

    computedApplicationDescription () {
      return appDescrition => {
        return ' | ' + this.$t(appDescrition)
      }
    },

    computedBaseURL () {
      return appBaseUrl => {
        return ' | ' + this.$t(appBaseUrl)
      }
    },

    computedServiceArtifactVersion () {
      return appId => {
        let html = '(artifact n/a)'
        if (this.serviceConfiguration[appId] && this.serviceConfiguration[appId].baseURLVersion) {
          html = this.serviceConfiguration[appId].baseURLVersion
        }
        return ' | ' + html
      }
    },

    computedFlywayMigration () {
      return appId => {
        return this.serviceConfiguration[appId]?.baseURLflywayMigration ? ' | ' + this.serviceConfiguration[appId].baseURLflywayMigration : ''
      }
    },

    computedLabelSwitchUser () {
      return this.$t('label.aboutSwitchUser')
    },

    computedLabelSelectWorkspace () {
      return this.$t('label.aboutSelectWorkspace')
    },

    remoteLabel () {
      return remoteTracker => {
        return `${this.$t(remoteTracker.name)} ${remoteTracker.error ? '(server n/a)' : ''} | ${this.$t(remoteTracker.description)}`
      }
    },

    computedWorkspaces () {
      const items = []
      const workspaces = {}
      for (const appId in this.aboutApplications) {
        workspaces[appId] = this.aboutApplications[appId].workspaces
        items.push({ text: this.$t(this.aboutApplications[appId].name), disabled: true, level: 0 })
        items.push(
          ...this.getWorkspacesItems(workspaces[appId], [], appId)
        )
      }
      return items
    }
  },

  methods: {
    handleUserChange () {
      this.$store.dispatch('userDataStore/setUserId', this.selectedUser)
      this.$router.agilis.home()
    },

    async setAppConfigs (appId) {
      const appConfig = this.aboutApplications[appId]
      const url = Vue.axios.agilisUrlConfigurationMinified()
      // const url = Vue.axios.agilisUrlConfigurationForApplication(appConfig.id) // loading the full config, with i18n
      const endpoint = appConfig.baseURL
      if (endpoint) {
        try {
          const response = await Vue.axios.get(url, Vue.axios.agilisRequestConfig(url, endpoint))
          const app = response.data.applications[appId]
          // need Vue.set to make object reactive
          // https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
          Vue.set(this.serviceConfiguration, appConfig.id, isUndefined(app) ? null : {
            version: app.version,
            baseURLVersion: app.baseURLVersion,
            baseURLflywayMigration: app.baseURLflywayMigration
          })
        } catch (error) {
          // WARNING: do not output full error as it contains the security token
          Vue.prototype.$debug(error.message)
        }
        if (appConfig.trackerTypesRemote) {
          this.checkRemoteTrackers(appConfig.trackerTypesRemote, appConfig.id, endpoint)
        }
      } else {
        Vue.set(this.serviceConfiguration, appConfig.id, null)
      }
    },

    checkRemoteTrackers (remoteTrackers, appId, endpoint) {
      for (const remote in remoteTrackers) {
        const url = Vue.axios.agilisUrlTrackerStatusCheck(appId, remote, remoteTrackers[remote].statusCheckId)
        Vue.axios
          .get(url, Vue.axios.agilisRequestConfig(url, endpoint))
          .then(() => {
            Vue.set(this.remoteTrackers, appId, remoteTrackers[remote])
          })
          .catch((error) => {
            Vue.set(this.remoteTrackers, appId, { ...remoteTrackers[remote], error: true })
            // WARNING: do not output full error as it contains the security token
            Vue.prototype.$debug(error.message)
          })
      }
    },

    getWorkspacesItems (workspaces, items = [], appId, level = 1) {
      for (const workspace in workspaces) {
        if (workspaces[workspace].workspaces) {
          items.push({
            text: this.$t(workspaces[workspace].name),
            value: appId + '_' + workspaces[workspace].id,
            workspaceId: workspaces[workspace].id,
            appId,
            level
          })
          this.getWorkspacesItems(workspaces[workspace].workspaces, items, appId, level + 1)
        } else {
          if (workspaces[workspace].id) {
            items.push({
              text: this.$t(workspaces[workspace].name),
              value: appId + '_' + workspaces[workspace].id,
              workspaceId: workspaces[workspace].id,
              appId,
              level
            })
          }
        }
      }
      return items
    },

    handleWorkspaceChange (value, event) {
      const workspaceUsers = Configuration.auth.findWorkspace(value.appId, value.workspaceId, false)?.users
      const workspaceRoles = {}
      if (workspaceUsers) {
        for (const userId in workspaceUsers) {
          const userRoleArray = workspaceUsers[userId].roles
          userRoleArray.forEach(roleId => {
            if (!workspaceRoles[roleId]) {
              workspaceRoles[roleId] = []
            }
            if (workspaceRoles[roleId].indexOf(userId) === -1) {
              workspaceRoles[roleId].push(userId)
              workspaceRoles[roleId].sort()
            }
          })
        }
      }
      this.workspaceRoles = workspaceRoles
    },

    handleUserClick (user) {
      this.selectedUser = user
      this.handleUserChange()
    },

    userCanAccessSettings (applicationId) {
      return Configuration.auth.userCanAccessSettings(applicationId)
    }
  }
}
